import BaseComponent from './BaseComponent';
import GalleryItem from './GalleryItem';

import gsap from 'gsap';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import Panzoom from '@panzoom/panzoom';

export default class DigitalGallery extends BaseComponent {
	constructor(element) {
		super();
		this.el = element;

		this.items = [...this.el.querySelectorAll('[data-gallery-item]')].map((item) => new GalleryItem(item));

		this.panzoomEl = null;

		// Modal Selectors
		this.modal = document.querySelector('[data-gallery-modal]');
		if (this.modal) {
			this.modalContainer = this.modal.querySelector('[data-gallery-modal-container]');
			this.modalCloseButton = this.modal.querySelector('[data-modal-close]');

			this.modalImage = this.modal.querySelector('[data-modal-image]');
			this.zoomInButton = this.modal.querySelector('[data-zoom-in]');
			this.zoomOutButton = this.modal.querySelector('[data-zoom-out]');

			this.initModalEvents();
			this.initZoomControls();
		}

		this.currentScroll = window.scrollY;
		this.ticking = false;

		this.handleScroll = this.handleScroll.bind(this);
		this.update = this.update.bind(this);

		this.breakpoint = window.matchMedia('(max-width:767px)');

		this.addListener(window, 'scroll', this.handleScroll);
		this.update();
	}

	initModalEvents() {
		// Listen for internal child bubble clicks
		this.addListener(this.el, 'galleryItemOpen', (e) => {
			this.openModal(e.detail);
		});

		this.addListener(this.modalCloseButton, 'click', () => this.closeModal());
	}

	initZoomControls() {
		this.zoomInButton.addEventListener('click', () => {
			if (this.panzoomEl) {
				this.panzoomEl.zoomIn();
			}
		});

		this.zoomOutButton.addEventListener('click', () => {
			if (this.panzoomEl) {
				this.panzoomEl.zoomOut();
			}
		});
	}

	openModal(data) {
		this.modalImage.onload = null;
		// Inject data into global markup instances
		this.modalImage.setAttribute('src', data.imageSrc);
		this.modalImage.setAttribute('alt', data.title);
		this.modal.querySelector('[data-modal-title]').textContent = data.title;
		this.modal.querySelector('[data-modal-school]').textContent = data.school;
		this.modal.querySelector('[data-modal-province]').textContent = data.province;
		this.modal.querySelector('[data-modal-text]').innerHTML = data.text;

		const studentsContainer = this.modal.querySelector('[data-modal-students-container]');
		const studentsText = this.modal.querySelector('[data-modal-students]');
		if (data.students && data.students.trim() !== '') {
			studentsText.textContent = data.students;
			studentsContainer.style.display = 'block';
		} else {
			studentsContainer.style.display = 'none';
		}

		// Reset scroll position to top
		if (this.modalScrollTarget) {
			this.modalScrollTarget.scrollTop = 0;
		}

		const setupPanzoom = () => {
			this.panzoomEl = Panzoom(this.modalImage, {
				maxScale: 4,
				minScale: 1,
				contain: 'outside',
				canvas: true,
			});
		};

		// Check if image is already cached/loaded, otherwise wait for paint execution
		if (this.modalImage.complete && this.modalImage.naturalWidth !== 0) {
			setupPanzoom();
		} else {
			this.modalImage.onload = () => {
				setupPanzoom();
			};
		}

		this.modal.classList.add('is-open');
		this.modal.setAttribute('aria-hidden', 'false');

		disablePageScroll(this.modalScrollTarget);

		gsap.killTweensOf(this.modalContainer);

		gsap.timeline().to(this.modalContainer, {
			y: 0,
			duration: 0.75,
			ease: 'expo.out',
		});
	}

	closeModal() {
		gsap.timeline({
			onComplete: () => {
				this.modal.classList.remove('is-open');
				this.modal.setAttribute('aria-hidden', 'true');
				enablePageScroll(this.modalScrollTarget);

				if (this.panzoomEl) {
					this.panzoomEl.destroy();
					this.panzoomEl = null;
				}

				this.modal.querySelector('[data-modal-image]').setAttribute('src', '');
			},
		}).to(this.modalContainer, {
			y: '100%',
			duration: 0.5,
			ease: 'power3.in',
		});
	}

	handleScroll() {
		this.currentScroll = window.scrollY;

		if (!this.ticking) {
			requestAnimationFrame(this.update);
			this.ticking = true;
		}
	}

	update() {
		this.items.forEach((item) => {
			item.update(this.currentScroll);
		});

		this.ticking = false;
	}

	destroy() {
		if (this.panzoomEl) {
			this.panzoomEl.destroy();
			this.panzoomEl = null;
		}
		this.items.forEach((item) => item.destroy());
		super.destroy();
	}
}
